import { describeError } from '@/lib/form/errorMessages';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Permission,
  catalogHardwareAttributes,
  type CatalogHardwareAttributes,
  type CatalogHardwareListItem,
  type CatalogItemDetails,
  type CreateCatalogItemRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, FormActions, FormErrorBanner } from '@/components/form';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';
import { HardwareAutosuggest } from '@/features/project-items/shared/pickers/CatalogAutosuggest';

const decimalRegex = /^\d+(\.\d+)?$/;
const decimalString = z.string().regex(decimalRegex);
const positiveDecimalString = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});
const positiveIntegerString = z
  .string()
  .regex(/^\d+$/, 'Expected positive integer')
  .refine((v) => parseInt(v, 10) > 0, { message: 'Must be greater than 0' });
const requiredCatalogId = z.string().min(1, 'Choose a value from the suggestions');

const formSchema = z.object({
  name: z.string().trim().min(1).max(200),
  hardwareCatalogId: requiredCatalogId,
  hardwareName: z.string().trim().max(200).optional(),
  sku: z.string().trim().max(80).optional(),
  manufacturer: z.string().trim().max(120).optional(),
  quantity: positiveIntegerString,
  coefficient: positiveDecimalString,
  unitPriceNet: decimalString,
  currency: z.enum(['RON', 'EUR', 'USD']).default('RON'),
});
type FormValues = z.infer<typeof formSchema>;

function defaults(): FormValues {
  return {
    name: '',
    hardwareCatalogId: '',
    hardwareName: '',
    sku: '',
    manufacturer: '',
    quantity: '1',
    coefficient: '2.5',
    unitPriceNet: '0.00',
    currency: 'RON',
  };
}

function parseAttributes(item: CatalogItemDetails | undefined): CatalogHardwareAttributes {
  if (!item?.attributes) return {};
  const parsed = catalogHardwareAttributes.safeParse(item.attributes);
  return parsed.success ? parsed.data : {};
}

function integerFromDecimal(value: string | undefined, fallback = '1'): string {
  if (!value) return fallback;
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return String(Math.max(1, Math.trunc(parsed)));
}

function valuesFromItem(item: CatalogItemDetails): FormValues {
  const attrs = parseAttributes(item);
  return {
    ...defaults(),
    name: item.name,
    hardwareCatalogId: attrs.hardwareCatalogId ?? '',
    hardwareName: attrs.hardwareName ?? item.material ?? '',
    sku: attrs.sku ?? item.sku ?? '',
    manufacturer: attrs.manufacturer ?? item.manufacturer ?? '',
    quantity: integerFromDecimal(attrs.quantity),
    coefficient: attrs.coefficient ?? '2.5',
    unitPriceNet: attrs.pricePerPiece ?? item.defaultCostNet ?? '0.00',
    currency: attrs.currency ?? item.currency,
  };
}

function buildBody(
  values: FormValues,
  mode: 'create' | 'edit',
): CreateCatalogItemRequest | UpdateCatalogItemRequest {
  const attributes: CatalogHardwareAttributes = {
    hardwareCatalogId: values.hardwareCatalogId || null,
    hardwareName: values.hardwareName || undefined,
    sku: values.sku || undefined,
    manufacturer: values.manufacturer || undefined,
    pricePerPiece: values.unitPriceNet,
    currency: values.currency,
    quantity: values.quantity,
    coefficient: values.coefficient,
  };
  const body = {
    name: values.name,
    sku: values.sku || undefined,
    material: values.hardwareName || undefined,
    manufacturer: values.manufacturer || undefined,
    defaultCostNet: values.unitPriceNet,
    currency: values.currency,
    attributes: attributes as Record<string, unknown>,
  };
  return mode === 'create' ? { type: 'HARDWARE' as const, ...body } : body;
}

interface Props {
  mode: 'create' | 'edit';
}

export function CatalogHardwareFormPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? params.id : undefined;
  const { user } = useAuth();
  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults(),
  });

  useEffect(() => {
    if (mode !== 'edit' || !itemQuery.data) return;
    form.reset(valuesFromItem(itemQuery.data));
  }, [form, itemQuery.data, mode]);

  const isReadOnly = mode === 'edit' && !canUpdate;
  const submitting = mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = mode === 'create' ? createMut.error : updateMut.error;

  const onHardwareSelected = (item: CatalogHardwareListItem | null) => {
    form.setValue('hardwareName', item?.name ?? '', { shouldDirty: true });
    form.setValue('sku', item?.sku ?? '', { shouldDirty: true });
    form.setValue('manufacturer', item?.manufacturer ?? '', { shouldDirty: true });
    form.setValue('unitPriceNet', item?.pricePerPiece ?? '0.00', {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('currency', item?.currency ?? 'RON', { shouldDirty: true });
    if (item && !form.getValues('name')) {
      form.setValue('name', item.name, { shouldDirty: true, shouldValidate: true });
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (mode === 'create') {
        await createMut.mutateAsync(buildBody(values, mode) as CreateCatalogItemRequest);
        void navigate({ to: '/hardware', search: {} });
        return;
      }
      if (!id) return;
      await updateMut.mutateAsync(buildBody(values, mode) as UpdateCatalogItemRequest);
    } catch {
      /* surfaced via mutation errors */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: '/hardware', search: {} });
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  if (mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  const titleKey = mode === 'create' ? 'hardware.create.title' : 'hardware.edit.title';

  return (
    <div className="w-full space-y-4">
      <Link
        to="/hardware"
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t('hardware.backToList')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{t(titleKey)}</h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <TextField name="name" label={t('projectItems.hardware.fields.name')} autoFocus />
          <HardwareAutosuggest
            name="hardwareCatalogId"
            label={t('projectItems.hardware.fields.hardwareCatalogId')}
            placeholder={t('projectItems.hardware.fields.hardwareCatalogPlaceholder')}
            required
            onSelectedOption={onHardwareSelected}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="quantity"
              label={t('projectItems.hardware.fields.quantity')}
              type="number"
              min={1}
              step={1}
            />
            <TextField
              name="coefficient"
              label={t('projectItems.hardware.fields.coefficient')}
              type="number"
              min={0.01}
              step="0.1"
            />
          </div>
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(mode === 'create' ? 'hardware.create.submit' : 'common.save')}
            isSubmitting={submitting}
            submitDisabled={!form.formState.isDirty && mode === 'edit'}
          >
            <Link to="/hardware" search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
